import React, { useState, createContext, useContext, useEffect } from 'react';
import { 
  Home, 
  UserRound, 
  Settings, 
  BarChart3, 
  UsersRound, 
  Bell,
  CalendarDays,
  CreditCard,
  ArrowRightFromLine,
  ArrowLeftFromLine,
  FolderClosed,
  X,
  Edit,
  Trash2,
  Save
} from 'lucide-react';
import UserListComponent from './manuela-users';
import FullPageCalendar from './manuela-calendar';
import MeetingsList from './manuela-meetings';
import FilesList from './manuela-files';
import Dashboard from './manuela-dashboard';
import Analytics from './manuela-analytics';
import TestPage from './manuela-test';

// Create a context for the drawer
const DrawerContext = createContext();

// Custom hook to use the drawer
export const useDrawer = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawer must be used within DrawerProvider');
  }
  return context;
};

const DrawerProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [title, setTitle] = useState('');

  const openDrawer = (title, content) => {
    setTitle(title);
    setContent(content);
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setIsOpen(false);
    // Clear content after animation completes
    setTimeout(() => {
      setContent(null);
      setTitle('');
    }, 300);
  };

  return (
    <DrawerContext.Provider value={{ openDrawer, closeDrawer, isOpen }}>
      {children}
      
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-500 z-40 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeDrawer}
      />
      
      {/* Drawer */}
      <div className={`fixed top-0 right-0 h-full bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } w-150`}>
        {/* Top Bar */}
        <div className="relative h-14 bg-gray-50 border-b">
          {/* Close button positioned at top right */}
          <button
            onClick={closeDrawer}
            className="absolute top-2 right-2 p-2 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label="Close drawer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Drawer Header with Title */}
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">{title}</h2>
        </div>
        
        {/* Drawer Content */}
        <div className="p-6 overflow-y-auto h-[calc(100%-140px)]">
          {content}
        </div>
      </div>
    </DrawerContext.Provider>
  );
};

// Billing Component
const BillingComponent = () => (
  <div className="p-6">
    <h1 className="text-3xl font-bold mb-6">Billing</h1>
    <div className="bg-white rounded-lg shadow p-6">
      <p className="text-gray-600">Manage billing, invoices, and payment methods.</p>
    </div>
  </div>
);

// Notifications Component
const NotificationsComponent = () => (
  <div className="p-6">
    <h1 className="text-3xl font-bold mb-6">Notifications</h1>
    <div className="bg-white rounded-lg shadow p-6">
      <p className="text-gray-600">Configure notification preferences and view history.</p>
    </div>
  </div>
);

// Settings Component
const SettingsComponent = () => (
  <div className="p-6">
    <h1 className="text-3xl font-bold mb-6">Settings</h1>
    <div className="bg-white rounded-lg shadow p-6">
      <p className="text-gray-600">Configure system settings and preferences.</p>
    </div>
  </div>
);

// Main Admin Dashboard Component
const ManuelaDashboard = ({ basePath = '/manuela' }) => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  // Menu structure with custom URLs and components
  const menuStructure = [
    {
      type: 'section',
      title: 'Main',
      items: [
        { 
          id: 'dashboard', 
          icon: Home, 
          label: 'Dashboard',
          path: '/dashboard',
          component: Dashboard
        },
        { 
          id: 'analytics', 
          icon: BarChart3, 
          label: 'Analytics',
          path: '/analytics',
          component: Analytics
        },
      ]
    },
    {
      type: 'section',
      title: 'Management',
      items: [
        { 
          id: 'users', 
          icon: UserRound, 
          label: 'Users',
          path: '/users',
          component: UserListComponent
        },
        { 
          id: 'calendar', 
          icon: CalendarDays, 
          label: 'Calendar',
          path: '/calendar',
          component: FullPageCalendar
        },
        { 
          id: 'meetings', 
          icon: UsersRound, 
          label: 'Meetings',
          path: '/meetings',
          component: MeetingsList
        },
        { 
          id: 'files', 
          icon: FolderClosed, 
          label: 'Files',
          path: '/files',
          component: FilesList
        },
      ]
    },
    {
      type: 'section',
      title: 'System',
      items: [
        { 
          id: 'billing', 
          icon: CreditCard, 
          label: 'Billing',
          path: '/billing',
          component: BillingComponent
        },
        { 
          id: 'notifications', 
          icon: Bell, 
          label: 'Notifications',
          path: '/notifications',
          component: NotificationsComponent
        },
        { 
          id: 'settings', 
          icon: Settings, 
          label: 'Settings',
          path: '/settings',
          component: SettingsComponent
        },
        { 
          id: 'test', 
          icon: Settings, 
          label: 'Test',
          path: '/test',
          component: TestPage
        },
      ]
    }
  ];

  // Flatten all menu items for easier access
  const allMenuItems = menuStructure.flatMap(section => section.items);

  // Get current path from URL
  const getCurrentPath = () => {
    // In a real app, you'd use React Router or similar
    // This is a simplified version using the hash
    const hash = window.location.hash.slice(1) || '/dashboard';
    return hash;
  };

  const [currentPath, setCurrentPath] = useState(getCurrentPath());

  // Find active menu item based on current path
  const activeMenuItem = allMenuItems.find(item => 
    `${basePath}${item.path}` === `${basePath}${currentPath}`
  ) || allMenuItems[0];

  // Update URL when navigation occurs
  const navigateTo = (path) => {
    // In a real app, you'd use React Router's navigate function
    // This is a simplified version using the hash
    window.location.hash = path;
    setCurrentPath(path);
  };

  // Listen for URL changes
  useEffect(() => {
    const handleHashChange = () => {
      setCurrentPath(getCurrentPath());
    };

    window.addEventListener('hashchange', handleHashChange);
    
    // Set initial hash if none exists
    if (!window.location.hash) {
      window.location.hash = '/dashboard';
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Render the active component
  const renderContent = () => {
    if (activeMenuItem && activeMenuItem.component) {
      const Component = activeMenuItem.component;
      return <Component />;
    }
    
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-6">Page Not Found</h1>
        <p className="text-gray-600">The requested page could not be found.</p>
      </div>
    );
  };

  return (
    <DrawerProvider>
      <div className="flex h-screen bg-gray-100">
        {/* Sidebar */}
        <div 
          className={`bg-gray-900 text-white transition-all duration-300 ease-in-out ${
            isSidebarExpanded ? 'w-64' : 'w-20'
          } relative overflow-hidden`}
        >
          <div className="p-4">
            {/* Toggle Button */}
            <button
              onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
              className="w-12 h-12 p-2 mb-4 rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center"
            >
              {isSidebarExpanded ? (
                <ArrowLeftFromLine className="w-5 h-5" />
              ) : (
                <ArrowRightFromLine className="w-5 h-5" />
              )}
            </button>
            
            {/* Admin Panel Logo */}
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">A</span>
              </div>
              <span className={`ml-3 text-xl font-semibold whitespace-nowrap transition-all duration-300 ${
                isSidebarExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}>
                Admin Panel
              </span>
            </div>
            
            <nav>
              {menuStructure.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  {/* Section Title/Divider */}
                  {sectionIndex >= 0 && (
                    <div className="relative my-4">
                      <div className="border-t border-gray-700"></div>
                      {section.title && (
                        <div className={`absolute top-1/2 left-0 transform -translate-y-1/2 transition-all duration-300 ${
                          isSidebarExpanded ? 'opacity-100' : 'opacity-0'
                        }`}>
                          <span className="ml-4 px-3 bg-gray-900 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            {section.title}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Menu Items in Section */}
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeMenuItem?.id === item.id;
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => navigateTo(item.path)}
                        className={`w-full flex items-center px-3 py-3 mb-2 rounded-lg transition-all duration-200 ${
                          isActive 
                            ? 'bg-blue-600 text-white' 
                            : 'hover:bg-gray-800 text-gray-300 hover:text-white'
                        }`}
                      >
                        <Icon className="w-6 h-6 flex-shrink-0" />
                        <span className={`ml-3 whitespace-nowrap transition-all duration-300 ${
                          isSidebarExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                        }`}>
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {renderContent()}
        </div>
      </div>
    </DrawerProvider>
  );
};

export default ManuelaDashboard;